import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { Subscription, SubscriptionAddon } from '../types/subscription';
import { FeatureKey } from '../services/featureService';

interface SubscriptionContextType {
  subscription: Subscription | null;
  addons: SubscriptionAddon[];
  isLoading: boolean;
  error: string | null;
  hasActiveSubscription: () => boolean;
  canAccessFeature: (feature: FeatureKey) => boolean;
  hasAddon: (addonType: string) => boolean;
  refreshSubscription: () => void;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const subscriptionData = useSubscription();

  // Set up global refresh function
  useEffect(() => {
    setGlobalRefreshSubscription(subscriptionData.refreshSubscription);
  }, [subscriptionData.refreshSubscription]);

  const contextValue = {
    ...subscriptionData,
    hasAddon: subscriptionData.hasAddon || (() => false), // Fallback if not available
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscriptionContext = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscriptionContext must be used within a SubscriptionProvider');
  }
  return context;
};

// Global subscription refresh function
let globalRefreshSubscription: (() => void) | null = null;

export const setGlobalRefreshSubscription = (refreshFn: () => void) => {
  globalRefreshSubscription = refreshFn;
};

export const triggerGlobalSubscriptionRefresh = () => {
  if (globalRefreshSubscription) {
    globalRefreshSubscription();
  }
};
